import { CalendarOff, ClipboardList } from 'lucide-react';
import { useState } from 'react';

import { EmployeeJobCard } from '@/components/employee/EmployeeJobCard';
import { EmployeePage } from '@/components/employee/EmployeePage';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { Pagination } from '@/components/ui/pagination';
import { useMyJobs } from '@/hooks/employee-portal';
import { JOB_STATUS_LABELS, type JobStatusValue } from '@shared/schemas/job';
import { DEFAULT_PAGE_SIZE } from '@shared/schemas/pagination';

/*
 * /employee/jobs — the list behind ROADMAP.md §6.3's detail screen.
 *
 * **Assigned jobs only**, and that is the server's answer, not this page's: the
 * endpoint scopes every read by an `EXISTS` against `job_assignment`, so there
 * is no query string this page could send to see somebody else's work.
 * FINDINGS.md §11.3 records that the prototype gets this right too.
 *
 * Soonest first, unlike the admin list. The office looks backwards through
 * history; a cleaner looks forward at what they have to drive to next.
 */

const STATUS_FILTERS: { value: JobStatusValue | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'scheduled', label: JOB_STATUS_LABELS.scheduled },
  { value: 'in_progress', label: JOB_STATUS_LABELS.in_progress },
  { value: 'completed', label: JOB_STATUS_LABELS.completed },
];

export function MyJobsPage() {
  const [status, setStatus] = useState<JobStatusValue | 'all'>('all');
  const [page, setPage] = useState(1);

  const jobs = useMyJobs({ page, status });
  const filtered = status !== 'all';

  return (
    <EmployeePage title="My Jobs" description="Every job you are on, soonest first.">
      <div className="mb-4">
        <FilterPills
          label="Filter your jobs by status"
          options={STATUS_FILTERS}
          value={status}
          onChange={(next) => {
            setStatus(next);
            setPage(1);
          }}
        />
      </div>

      {/*
        Three branches, and they are different things — the P22 rule, which was
        found on this portal. A failed request says so and offers to retry; it
        never renders as "you have no jobs", which is a sentence a cleaner would
        believe and go home on.
      */}
      {jobs.error ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="We could not load your jobs"
            hint="Something went wrong on our side — this does not mean your list is empty. Try again, and ring the office if it keeps happening."
            onRetry={() => {
              void jobs.refetch();
            }}
          />
        </div>
      ) : jobs.isPending ? (
        <ul aria-busy="true" className="space-y-3">
          <span className="sr-only">Loading your jobs</span>
          {[0, 1, 2].map((key) => (
            <li
              key={key}
              aria-hidden="true"
              className="h-[188px] animate-pulse rounded-lg bg-ink-100"
            />
          ))}
        </ul>
      ) : jobs.data && jobs.data.data.length > 0 ? (
        <ul className="space-y-3">
          {jobs.data.data.map((job) => (
            <EmployeeJobCard key={job.id} job={job} />
          ))}
        </ul>
      ) : (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <EmptyState
            icon={filtered ? ClipboardList : CalendarOff}
            title={filtered ? 'Nothing matches that filter.' : 'You are not on any jobs yet.'}
            hint={
              filtered
                ? 'Try showing all statuses.'
                : 'When the office puts you on a job it appears here, with its checklist and the address.'
            }
          />
        </div>
      )}

      {jobs.data ? (
        <Pagination
          page={jobs.data.page}
          pageSize={jobs.data.pageSize || DEFAULT_PAGE_SIZE}
          total={jobs.data.total}
          onPageChange={setPage}
          itemNoun="jobs"
        />
      ) : null}
    </EmployeePage>
  );
}
